import axios, { type AxiosResponse } from "axios";
import React, { type ReactElement } from "react";
import { useLocation, useParams } from "react-router-dom";

import apiRoutes from "../../../../constants/api-routes";
import useAuthHeader from "../../../../hooks/useAuthHeader";
import useResolvedPluginDisplayName from "../../../../hooks/useResolvedPluginDisplayName";
import useResourceVerifier from "../../../../hooks/useResourceVerifier";
import { ErrorBoundary, ErrorCard, LoadingCard } from "../../../errors";
import { validatePluginId } from "../../../errors/pluginValidation";

interface PluginResource {
	pluginId?: string;
	name?: string;
	active?: boolean;
	isCustomerManaged?: boolean;
	[key: string]: unknown;
}

interface PluginGroupsWithErrorBoundaryProps {
	children: ReactElement;
}

const PluginGroupsWithErrorBoundary = ({ children }: PluginGroupsWithErrorBoundaryProps): React.JSX.Element => {
	const { pluginId = "" } = useParams<{ pluginId: string }>();
	const location = useLocation();
	const authHeader = useAuthHeader();

	const {
		isLoading,
		resourceError: pluginError,
		resourceData: pluginData,
	} = useResourceVerifier<PluginResource>({
		id: pluginId,
		validateIdFn: validatePluginId,
		fetchResourceFn: () =>
			axios.get(apiRoutes.getPlugin(pluginId), { headers: authHeader }),
		validateResponseFn: (response: AxiosResponse<PluginResource>) =>
			Boolean(response?.data && !response.data.error && response.data.pluginId),
		authHeader,
		invalidIdMsg: "Invalid plugin ID format",
		timeoutMsg: "Timeout. Plugin may not exist or has been removed.",
	});

	const { displayName, isResolvingName } = useResolvedPluginDisplayName(pluginId, pluginData);

	const canRenderChildren =
		!isLoading
		&& !pluginError
		&& pluginData
		&& location.state
		&& typeof location.state === "object";

	const getStateForChildren = (): Record<string, unknown> => {
		if (!location.state && pluginData) {
			return {
				name: displayName,
				isActive: Boolean(pluginData.active),
				isCustomerManaged: pluginData.isCustomerManaged,
			};
		}
		return (location.state ?? {}) as Record<string, unknown>;
	};

	if (isLoading) {
		return (
			<LoadingCard
				title="Loading Plugin Groups"
				message="Please wait while we load the plugin groups..."
				subMessage="If this takes more than a few seconds, the plugin may not exist."
			/>
		);
	}

	if (pluginError) {
		return (
			<ErrorCard
				title="Plugin Groups Error"
				message={pluginError.message}
				returnPath="/settings/plugins"
				returnButtonText="Return to Plugins"
			/>
		);
	}

	if (!location.state && pluginData && isResolvingName) {
		return (
			<LoadingCard
				title="Loading Plugin Groups"
				message="Please wait while we load plugin details..."
				subMessage="If this takes more than a few seconds, try again."
			/>
		);
	}

	return (
		<ErrorBoundary parentPath="/settings/plugins">
			{React.cloneElement(children, {
				...(children.props as Record<string, unknown>),
				...(canRenderChildren ? {} : { state: getStateForChildren() }),
			})}
		</ErrorBoundary>
	);
};

export default PluginGroupsWithErrorBoundary;
